import React from 'react';

import { useSelector } from 'react-redux';

import getConfig from 'next/config';

import _isEqual from 'lodash/isEqual';

import { useDevice } from '@bonnet/next/device';

import { useBrand } from '@atc/brand';
import { useFeatures } from '@atc/features';

import {
    Panel,
    Text,
} from 'reaxl';
import { ConnectedEmailOwner, EmailCTA } from 'reaxl-email';
import { EsntialBuyNowCTA } from 'reaxl-wallet';

import { fallbackInterestRates } from 'axl-config';

import interestRateDuck from '@/ducks/interestRateDuck.js';
import paymentsDuck from '@/ducks/paymentsDuck.js';
import vdpResultsDuck from '@/ducks/vdp/vdpResultsDuck.js';

import saveDealXgFetcher from '@/fetchers/ExportToDealXgFetcher';

import OwnerDetailsSnapshotContainer from './OwnerDetailsSnapshotContainer';
import PreQualCTAContainer from './PreQualCTAContainer';
import ShowcaseAdSlotContainer from './ShowcaseAdSlotContainer';

function DealerLeadsContainer({
    emailCTAProps,
    emailOwnerProps,
    persistDealData,
    phoneAndTextHandler,
}) {
    const device = useDevice();
    const isXs = device?.is?.xs ?? false;
    const isGreaterThanSm = device?.greaterThan?.sm ?? false;
    const isLg = device?.is?.lg ?? false;

    const { brands, isBrand } = useBrand();
    const isKbbBranded = isBrand(brands.KBB_BRAND);
    const isAtcBranded = isBrand(brands.ATC_BRAND);

    // Feature Flags
    const {
        ads: [isAdsEnabled, { mrec: isMrecEnabled }],
        chat: [, { enable_mobile_chat: isMobileChatEnabled }],
        collapse_vdp_email_form: [collapseVdpEmailForm],
    } = useFeatures([
        'ads',
        'chat',
        'collapse_vdp_email_form',
    ]);
    const { publicRuntimeConfig } = getConfig();
    const listing = useSelector(vdpResultsDuck.selectors.getVdpActiveInventory) || {};
    const nationalRates = useSelector(interestRateDuck.selectors.getDuckState) || fallbackInterestRates;

    const {
        dealerConfig: {
            hideAds,
        } = {},
        ecommWebsite,
        esntial,
        id: listingId,
        isWestlakePrequal = false,
        marketExtension: { parentOwnerId: parentId } = {},
        owner = {},
        priority,
    } = listing;

    const ownerTrackingId = parentId ?? (typeof owner === 'number' ? owner : owner.id);

    const { hideEmailForm } = owner;

    const { onClick: onEmailCTAClick } = emailCTAProps;

    // enable Westlake Prequal CTA in mobile state
    const enableMobileWestlakeCTA = isWestlakePrequal && isXs;

    // Ads
    let showMrec = false;
    if (isAdsEnabled && !hideAds) {
        showMrec = isMrecEnabled && (isAtcBranded && isGreaterThanSm) || (isKbbBranded && isLg);
    }

    const collapseEmailForm = collapseVdpEmailForm && ['FREE', 'STANDARD', 'STANDARD_PLUS', 'BASIC'].includes(priority);

    return (
        <>
            {!hideEmailForm && collapseEmailForm && !isXs && (
                <EmailCTA
                    context="inline"
                    labelKey="checkAvailability"
                    listingId={listingId}
                    onClick={onEmailCTAClick}
                    type="block"
                />
            )}
            {!hideEmailForm && !collapseEmailForm && (
                <Panel>
                    <Panel.Body>
                        <ConnectedEmailOwner
                            phoneClickHandler={isXs ? phoneAndTextHandler : undefined}
                            {...emailOwnerProps}
                            uiContext="default"
                        />

                        {esntial?.deemphasized && (
                            <>
                                <hr className="margin-bottom-4" />
                                <Text weight="bold">{esntial.helperText}</Text>
                                <EsntialBuyNowCTA
                                    dealsDuck={emailOwnerProps?.dealsDuck}
                                    label={ecommWebsite?.label}
                                    href={ecommWebsite?.href}
                                    analyticsData={{
                                        placement: 'dealer-card',
                                        listingId: listing?.id,
                                    }}
                                    className="margin-vertical-3"
                                    paymentsDuck={paymentsDuck}
                                    nationalRates={nationalRates}
                                    listing={listing}
                                    ownerTrackingId={ownerTrackingId}
                                    saveDealXgFetcher={saveDealXgFetcher}
                                    env={publicRuntimeConfig?.environment === 'prod' ? 'prod' : 'staging'}
                                    bsStyle="secondary"
                                    target={esntial ? '_self' : '_blank'}
                                    block
                                />

                            </>
                        )}
                    </Panel.Body>
                </Panel>
            )}
            {enableMobileWestlakeCTA && (
                <PreQualCTAContainer
                    uiContext="simplified"
                    emailCTAProps={esntial?.deemphasized ? undefined : emailCTAProps}
                />
            )}
            <OwnerDetailsSnapshotContainer
                uiContext={{
                    isPanel: true,
                    showColoredBackground: hideEmailForm,
                }}
                chatClickHandler={(!isXs || isMobileChatEnabled) ? persistDealData : undefined}
                onEmailCTAClick={onEmailCTAClick}
                phoneClickHandler={isXs ? phoneAndTextHandler : undefined}
            />
            {showMrec && <ShowcaseAdSlotContainer />}
        </>
    );
}

export default React.memo(DealerLeadsContainer, _isEqual);
